import { useEffect, useState, useRef } from "react";
import {
  Grid,
  Stack,
  useToast,
  Heading,
  Box,
  Flex,
  Button,
  Text,
  Input,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  IconButton,
  GridItem,
  Icon,
  Select,
  HStack,
} from "@chakra-ui/react";
import { Loading } from "../../../components/Loading";
import useDeleteToast from "../../../components/DeleteToast";
import { DeleteAlert } from "../../../components/DeleteAlert";
import { DashboardTotalCount } from "../../../components/DashboardTotalCount";
import { FiMoreVertical, FiSearch, FiUsers } from "react-icons/fi";
import { CgAddR } from "react-icons/cg";
import { Unauthorized } from "../../../components/Unauthorized";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Link, useParams } from "react-router-dom";
import {
  getVendorsList,
  deleteVendor,
} from "../../../actions/inventory/vendor.action";
import { FaSackDollar } from "react-icons/fa6";
import { FaFileInvoiceDollar, FaHandHoldingUsd } from "react-icons/fa";
import { TbArrowsSort, TbSettings } from "react-icons/tb";
import { ImFilter } from "react-icons/im";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import { LuSettings2 } from "react-icons/lu";
import { FaStar } from "react-icons/fa";

const vendorStatusMap = {
  active: "Active",
  inactive: "Inactive",
  suspended: "Suspended",
  under_review: "Under Review",
};

const statusColorMap = {
  active: "green",
  inactive: "gray",
  suspended: "red",
  under_review: "yellow",
};

const statusOptions = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
  { value: "under_review", label: "Under Review" },
];

const standardOptions = [10, 20, 50];

const Vendors = () => {
  const toast = useToast();
  const showDeleteToast = useDeleteToast();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    id: null,
  });
  const [rawData, setRawData] = useState([]);
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalVendors, setTotalVendors] = useState(0);
  const [error, setError] = useState();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDir, setSortDir] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const debounceTimer = useRef(null);
  const [totalPages, setTotalPages] = useState(1);
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const loadData = async (page, limit, search, status) => {
    try {
      setFetching(true);
      const {
        data: vendorsData,
        totalVendors,
        globalStats,
      } = await getVendorsList(page, limit, search, status);
      setTotalVendors(totalVendors);
      setGlobalStats(globalStats);
      setTotalPages(Math.ceil(totalVendors / limit));

      const formatted = vendorsData.map((item) => {
        const email = item?.email || item?.contactDetails?.email || "-";
        const mobileNumber =
          item?.mobileNumber || item?.contactDetails?.phoneNumber || "-";
        const status = item?.vendorStatus || "under_review";

        return {
          id: item._id,
          uniqueVendorCode: item?.uniqueVendorCode || "-",
          email: email,
          mobileNumber: mobileNumber,
          name: item?.vendorName || "-",
          vendorRating: item?.vendorRating || 0,
          fleetCount: item?.fleetCount || 0,
          vendorType: item?.vendorType || "-",
          gstState: item?.gstState || "-",
          status: status,
          statusColor: statusColorMap[status] || "gray",
        };
      });
      setRawData(formatted);
    } catch (error) {
      setError(error.message);
      toast({
        title: "Failed to fetch data!",
        description: error.message,
        status: "error",
        duration: 9000,
      });
    } finally {
      setFetching(false);
      setLoading(false);
    }
  };

  // Debounce search input → updates query state → triggers fetch effect
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setQuery(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  // Single effect — values passed directly so no stale closure possible
  useEffect(() => {
    loadData(currentPage, itemsPerPage, query, statusFilter);
  }, [query, currentPage, itemsPerPage, statusFilter]);

  const filteredSorted = (() => {
    let filtered = [...rawData];
    if (sortDir) {
      filtered = filtered.sort((a, b) => {
        if (!a.name || !b.name) return 0;
        return sortDir === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      });
    }
    return filtered;
  })();

  const startIndex = (currentPage - 1) * itemsPerPage;

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages]);

  const perPageOptions = standardOptions;

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setQuery("");
    setCurrentPage(1);
  };

  const handleSort = () => {
    setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteVendor(deleteConfirm.id);
      showDeleteToast("Vendor deleted successfully");
      loadData(currentPage, itemsPerPage, query, statusFilter);
    } catch (err) {
      toast({
        title: "Failed to delete vendor",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  if (loading) return <Loading />;
  if (error === "Unauthorized user") return <Unauthorized />;

  return (
    <>
      <DeleteAlert
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onDelete={onDeleteConfirm}
        label="Vendor"
        itemName="Vendor"
      />
      <Stack w={"100%"} justifyContent={"center"} spacing={10} p={6}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <BreadCrumbMaker
            items={[
              { link: "", title: "Fleet Management" },
              { link: "/fleet-management/vendor", title: "Vendor", on: true },
            ]}
          />
          <Button
            size={"sm"}
            as={Link}
            to={"/fleet-management/vendor/add"}
            borderRadius={5}
            px={10}
            fontWeight={0}
            py={5}
          >
            <Flex gap={1} alignItems={"center"}>
              <CgAddR /> <Text>Add Vendor</Text>
            </Flex>
          </Button>
        </Flex>
        <Box>
          <Grid
            gridGap={6}
            templateColumns={{
              base: "repeat(2, 1fr)",
              md: "repeat(3, minmax(250px, 1fr))",
            }}
          >
            <GridItem key="VendorFleetOnDuty">
              <Box
                bg="white"
                p={4}
                rounded="md"
                textAlign="center"
                display="flex"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                h="120px"
                gap={2}
                borderRadius="3xl"
              >
                <Box
                  bg={"#FFF5D9"}
                  w={18}
                  h={18}
                  p={30}
                  mx={4}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                >
                  <Icon as={FaSackDollar} boxSize={6} color={"#FFBB38"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">
                    Vendor Fleet On Duty
                  </Text>
                  <Text fontSize="25px">
                    {globalStats?.onDuty || rawData?.filter((v) => String(v.status) === "1").length || 0}
                  </Text>
                </Flex>
              </Box>
            </GridItem>
            <GridItem key="IdleVendorFleet">
              <Box
                bg="white"
                p={4}
                rounded="md"
                textAlign="center"
                display="flex"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                h="120px"
                gap={2}
                borderRadius="3xl"
              >
                <Box
                  bg={"#E7EDFF"}
                  w={18}
                  h={18}
                  p={30}
                  mx={4}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                >
                  <Icon as={FaHandHoldingUsd} boxSize={6} color={"#396AFF"} />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">
                    Idle Vendor Fleet
                  </Text>
                  <Text fontSize="25px">
                    {globalStats?.idle || rawData?.filter((v) => String(v.status) === "2").length || 0}
                  </Text>
                </Flex>
              </Box>
            </GridItem>
            <GridItem key="VendorFleets">
              <Box
                bg="white"
                p={4}
                rounded="md"
                textAlign="center"
                display="flex"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                h="120px"
                gap={2}
                borderRadius="3xl"
              >
                <Box
                  bg={"#FFE0EB"}
                  w={18}
                  h={18}
                  p={30}
                  mx={4}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                >
                  <Icon
                    as={FaFileInvoiceDollar}
                    boxSize={6}
                    color={"#FF82AC"}
                  />
                </Box>
                <Flex flexDirection={"column"} alignItems={"flex-start"}>
                  <Text fontSize="16px" color="#718EBF">
                    Vendor Fleets
                  </Text>
                  <Text fontSize="25px">{globalStats?.total || totalVendors || 0}</Text>
                </Flex>
              </Box>
            </GridItem>
          </Grid>
        </Box>
        <Flex
          justifyContent={"space-between"}
          flexDirection={{ base: "column", md: "row" }}
        >
          <Heading fontSize={"xl"} py={4} color={"#333B69"}>
            Vendor List
          </Heading>
          <Flex w="100%" maxW="600px" gap={3} justifyContent={"flex-end"}>
            <Flex
              gap={0}
              alignItems="center"
              w="100%"
              maxW="300px"
              bg="white"
              px={3}
              h="40px"
              borderRadius={"13"}
            >
              <FiSearch color="#718EBF" fontSize="1rem" />
              <Input
                placeholder="Search here"
                border="none"
                onChange={(e) => setSearchInput(e.target.value)}
                _focus={{ outline: "none", boxShadow: "none" }}
                fontSize="sm"
                h="full"
                _placeholder={{ color: "#718EBF" }}
                value={searchInput}
              />
              {searchInput && (
                <IconButton
                  aria-label="Clear search"
                  icon={<Text fontSize="lg">×</Text>}
                  size="sm"
                  variant="ghost"
                  onClick={clearSearch}
                />
              )}
            </Flex>

            <Flex
              gap={2}
              alignItems="center"
              justifyContent="center"
              w="100%"
              maxW="91px"
              bg="white"
              px={3}
              h="40px"
              borderRadius={"13"}
              cursor="pointer"
              onClick={handleSort}
            >
              <TbArrowsSort color="#718EBF" fontSize="1rem" />
              <Text fontSize="sm" color="#718EBF">
                Sort {sortDir ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </Text>
            </Flex>
            <Flex
              gap={2}
              alignItems="center"
              justifyContent="center"
              w="100%"
              maxW="200px"
              bg="white"
              px={3}
              h="40px"
              borderRadius={"13"}
            >
              <ImFilter color="#718EBF" fontSize="1rem" />
              <Select
                fontSize="sm"
                width="230px"
                value={statusFilter}
                onChange={handleFilterChange}
                h="28px"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Flex>
            {/* <Flex
              alignItems="center"
              justifyContent="center"
              w="100%"
              maxW="40px"
              bg="white"
              h="40px"
              borderRadius="full"
              cursor="pointer"
            >
              <TbSettings color="#718EBF" fontSize="1.3rem" />
            </Flex> */}
          </Flex>
        </Flex>
        <Box p={7} bg={"white"} overflowX={"auto"} borderRadius={"3xl"}>
          <Table size="sm" bg={"white"} fontSize={"small"}>
            <Thead>
              <Tr>
                <Th color={"#718EBF"}>S.No</Th>
                <Th color={"#718EBF"}>Vendor ID</Th>
                <Th color={"#718EBF"}>Name</Th>
                <Th color={"#718EBF"}>Vendor Type</Th>
                <Th color={"#718EBF"}>Mobile Number</Th>
                <Th color={"#718EBF"}>State</Th>
                <Th color={"#718EBF"}>Fleet Count</Th>
                <Th color={"#718EBF"}>Status</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredSorted.length > 0 &&
                filteredSorted.map((item, index) => (
                  <Tr
                    key={item.id}
                    onClick={() =>
                      navigate(`/fleet-management/vendor/${item?.id}`)
                    }
                  >
                    <Td>{startIndex + index + 1}</Td>
                    <Td>{item?.uniqueVendorCode || "-"}</Td>
                    <Td>{item?.name || ""}</Td>
                    <Td>
                      {item?.vendorType
                        ? item.vendorType.charAt(0).toUpperCase() +
                          item.vendorType.slice(1)
                        : "-"}
                    </Td>
                    <Td>{item?.mobileNumber || ""}</Td>
                    <Td>{item?.gstState || "-"}</Td>
                    <Td>{item?.fleetCount || 0}</Td>
                    <Td>
                      <Badge
                        colorScheme={item?.statusColor}
                        px={3}
                        py={1}
                        rounded="lg"
                      >
                        {vendorStatusMap[item?.status] || item?.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <MenuList borderRadius={0}>
                          <MenuItem
                            as={Link}
                            to={`/fleet-management/vendor/edit/${item.id}`}
                            onClick={(e) => e.stopPropagation()}
                            borderRadius={0}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                            }}
                            borderRadius={0}
                            color="red.500"
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>

          {filteredSorted.length === 0 && (
            <Flex
              direction="column"
              alignItems="center"
              justifyContent="center"
              py={16}
              gap={3}
            >
              <Icon as={FiUsers} boxSize={10} color="#CBD5E0" />
              <Text fontSize="lg" fontWeight="semibold" color="#718EBF">
                No Vendors Found
              </Text>
              <Text fontSize="sm" color="#A0AEC0">
                {searchInput || statusFilter
                  ? "Try adjusting your search or filter to find what you're looking for."
                  : "No vendors have been added yet. Click 'Add Vendor' to get started."}
              </Text>
            </Flex>
          )}

          {/* Pagination — only shown when there are results */}
          {filteredSorted.length > 0 && (
            <Flex justifyContent="space-between" alignItems="center" mt={4} gap={2}>
              <Flex align="center" gap={4} wrap="wrap" justifyContent="center" mt={4}>
                <Text fontSize="sm" color={"#8B909A"}>
                  Showing:
                </Text>
                <Select
                  size="sm"
                  width="fit-content"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {perPageOptions.map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </Select>
                <Text fontSize="sm" color={"#8B909A"}>
                  {" "}
                  of {totalVendors}
                </Text>
              </Flex>
              <Flex alignItems="center" mt={4} gap={2}>
                <Button
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  isDisabled={currentPage === 1}
                >
                  <GoChevronLeft />
                </Button>
                {(() => {
                  let startPage = 1;
                  let endPage = totalPages;
                  if (totalPages > 5) {
                    if (currentPage <= 3) {
                      startPage = 1;
                      endPage = 5;
                    } else if (currentPage >= totalPages - 2) {
                      startPage = totalPages - 4;
                      endPage = totalPages;
                    } else {
                      startPage = currentPage - 2;
                      endPage = currentPage + 2;
                    }
                  }
                  const pagesToShow = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pagesToShow.push(
                      <Button
                        key={i}
                        size="sm"
                        variant={i === currentPage ? "solid" : ""}
                        colorScheme={i === currentPage ? "#0F60FF" : ""}
                        bgColor={i === currentPage ? "#0F60FF" : "#F1F2F6"}
                        onClick={() => setCurrentPage(i)}
                      >
                        <Text color={i === currentPage ? "#FFFFFF" : "#8B909A"}>
                          {i}
                        </Text>
                      </Button>
                    );
                  }
                  return pagesToShow;
                })()}
                <Button
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  isDisabled={currentPage === totalPages}
                >
                  <GoChevronRight />
                </Button>
              </Flex>
            </Flex>
          )}
        </Box>
      </Stack>
    </>
  );
};

export default Vendors;